import {
  LocalDateTime,
  localDateTimeToDate,
  LocalDate,
  timestampToDate,
  localDateToDate,
  localDateFromDate,
  localDateTimeFromDate,
  timestampFromDate,
  Timestamp,
} from "@flockcover/flock-svc-core-types"
import { format as dateFnsFormat, subDays, startOfDay, endOfDay } from "date-fns"

export const ISO_DATE_FORMAT = `yyyy-MM-dd'T'HH:mm:ss`
export enum DateFilter {
  TODAY = `today`,
  YESTERDAY = `yesterday`,
  LAST_7_DAYS = `last_7_days`,
  LAST_30_DAYS = `last_30_days`,
  CUSTOM = `custom`,
}

export const localDateTimeToString = (date: LocalDateTime, locale = `en`, format = `dd.MM.yyyy hh:mm aaa`): string => {
  const { timeZone } = Intl.DateTimeFormat(locale).resolvedOptions()
  return dateFnsFormat(localDateTimeToDate(date, timeZone), format)
}

export const localDateToString = (date: LocalDate, locale = `en`, format = `dd.MM.yyyy`): string => {
  const { timeZone } = Intl.DateTimeFormat(locale).resolvedOptions()
  return dateFnsFormat(localDateToDate(date, timeZone), format)
}

export const localDateToStringMid = (date: LocalDateTime, locale = `en`): string =>
  localDateTimeToString(date, locale, `do MMM yy`).replace(/(\d{2})$/, `'$1`)

export const localDateToStringLong = (date: LocalDateTime, locale = `en`): string =>
  localDateTimeToString(date, locale, `do MMMM yyyy`)

export const timestampToString = (date: Timestamp, format = `dd.MM.yyyy hh:mm aaa`): string =>
  dateFnsFormat(timestampToDate(date), format)
export const localDateFromString = (val: string) => localDateFromDate(new Date(val), `Europe/London`)

export const localDateTimeFromString = (val: string) => localDateTimeFromDate(new Date(val), `Europe/London`)

export const toDateRangeLabel = (dateFrom: Date, dateTo: Date): string =>
  `${dateFnsFormat(dateFrom, `dd MMM`)} - ${dateFnsFormat(dateTo, `dd MMM`)}`

export const timestampFromString = (val: string) => timestampFromDate(new Date(val))

export interface DateRange {
  fromDate: Date
  toDate: Date
}

export const isDateFilter = (value: string | null): value is DateFilter =>
  value !== null && Object.values(DateFilter).includes(value as DateFilter)

const getDateRange = (dateFilter: DateFilter) => {
  const today = getToday()
  const yesterday = subDays(today, 1)
  switch (dateFilter) {
    case DateFilter.TODAY:
      return {
        fromDate: startOfDay(today),
        toDate: endOfDay(today),
      }
    case DateFilter.YESTERDAY:
      return {
        fromDate: startOfDay(yesterday),
        toDate: endOfDay(yesterday),
      }
    case DateFilter.LAST_7_DAYS:
      return {
        fromDate: startOfDay(subDays(today, 7)),
        toDate: endOfDay(today),
      }
    case DateFilter.LAST_30_DAYS:
      return {
        fromDate: startOfDay(subDays(today, 30)),
        toDate: endOfDay(today),
      }
    default:
      throw new Error(`Invalid date filter ${dateFilter}`)
  }
}

const getToday = () => new Date()
const parseDate = (date: string) => new Date(`${date}T00:00:00Z`)
export const formatForApi = (date: Date) => `${dateFnsFormat(date, ISO_DATE_FORMAT)}Z`

export const parseDateRange = (
  dateFrom: DateFilter | string | null,
  dateTo: string | null,
  defaultDateRange: DateFilter = DateFilter.LAST_7_DAYS
): DateRange => {
  const defaultRange = getDateRange(defaultDateRange)
  if (isDateFilter(dateFrom)) return getDateRange(dateFrom)
  if (!dateFrom && !dateTo) return defaultRange
  const fromDate = dateFrom ? parseDate(dateFrom) : defaultRange.fromDate
  const toDate = dateTo ? parseDate(dateTo) : defaultRange.toDate
  return {
    fromDate: startOfDay(fromDate),
    toDate: endOfDay(toDate),
  }
}

/** Used to santitise dates for requests.
 * Eg 2023-02-07 => 07.02.2023
 * Eg 07.02.2023, true => 2023-02-07 */
export const sanitiseDate = (date: string) => {
  if ((!date.includes(`-`) && !date.includes(`.`)) || date.length !== 10) throw new Error(`Invalid date`)
  const inputIsDisplay = date.includes(`.`)
  return date
    .split(inputIsDisplay ? `.` : `-`)
    .reverse()
    .join(inputIsDisplay ? `-` : `.`)
}

export const reduceDateByOneHour = (dateString: LocalDateTime) => {
  const endDate = new Date(dateString)
  const endDateMinusOneHour = new Date(endDate.setHours(endDate.getHours() - 1)).toISOString() as LocalDateTime
  return endDateMinusOneHour
}

export const localDateTimesToStartEnd = (startTime: LocalDateTime, endTime: LocalDateTime) =>
  `${localDateToStringMid(startTime)} - ${localDateToStringMid(reduceDateByOneHour(endTime))}`
